import React, { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';

import useLiquidRenderer from '@hooks/shared/useLiquidRenderer';
import { Alert } from 'antd';

import TemplateIframe from './TemplateIframe';

interface PaperSize {
  width: number;
  height: number;
}

interface TemplatePreviewSplitPaneProps {
  visible: boolean;
  content?: string;
  sampleData?: Record<string, unknown>; // 🔥 外部輸入 data 的入口
  paperSize: PaperSize;
  cssVariablePrefix?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  namespace?: string;
  debounceMs?: number;
  scale?: number;
  iframeStyle?: CSSProperties;
  onRenderError?: (error: string | null) => void;
}

/**
 * TemplatePreviewSplitPane - Reusable split preview pane with auto-refresh
 *
 * @param props.visible - Whether preview pane is visible
 * @param props.content - Liquid template content (watched for changes)
 * @param props.sampleData - Sample data for rendering (EXTERNAL INPUT - 外部輸入 data 的入口)
 * @param props.paperSize - Paper size { width, height } in mm
 * @param props.cssVariablePrefix - CSS variable prefix ('paper' or 'label')
 * @param props.t - i18n translate function
 * @param props.namespace - i18n namespace (e.g., 'shared.order_templates')
 * @param props.debounceMs - Debounce delay in milliseconds (default: 500)
 * @param props.scale - Scale factor for preview (default: 0.7)
 * @param props.iframeStyle - Additional styles for iframe
 */
const TemplatePreviewSplitPane: React.FC<TemplatePreviewSplitPaneProps> = ({
  visible,
  content,
  sampleData, // 🔥 外部輸入 data 的入口
  paperSize,
  cssVariablePrefix = 'paper',
  t,
  namespace = 'shared.order_templates',
  debounceMs = 500,
  scale = 0.7,
  iframeStyle = {},
  onRenderError
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localHtml, setLocalHtml] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const useLiquidRendererTyped = useLiquidRenderer as unknown as (args: { sampleData?: Record<string, unknown> }) => {
    render: (content: string, sampleData: Record<string, unknown> | undefined) => Promise<string>;
  };
  const { render } = useLiquidRendererTyped({ sampleData });

  // Debounced render function
  const debouncedRender = useCallback((templateContent: string | undefined) => {
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(async () => {
      if (!templateContent || !templateContent.trim() || !sampleData) {
        setLocalHtml('');
        setLocalError(null);
        return;
      }

      try {
        const html = await render(templateContent, sampleData);
        setLocalHtml(html);
        setLocalError(null);
        onRenderError?.(null);
      } catch (error) {
        console.error('Split preview render error:', error);
        const message = (error as Error)?.message ?? String(error);
        setLocalError(message);
        onRenderError?.(message);
      }
    }, debounceMs);
  }, [sampleData, render, debounceMs]);

  // Watch content changes and trigger debounced render
  useEffect(() => {
    if (visible) {
      debouncedRender(content);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [content, visible, debouncedRender]);

  // Update iframe CSS variables when paperSize changes
  useEffect(() => {
    if (iframeRef.current) {
      try {
        const iframeDoc = iframeRef.current.contentDocument ||
                         iframeRef.current.contentWindow!.document;
        if (iframeDoc?.body) {
          iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-width`, `${paperSize.width}mm`);
          iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-height`, `${paperSize.height}mm`);

          // Update iframe height
          const contentHeight = iframeDoc.body.scrollHeight;
          iframeRef.current.style.height = `${contentHeight + 20}px`;
        }
      } catch (err) {
        console.error('Failed to update split preview iframe:', err);
      }
    }
  }, [paperSize, cssVariablePrefix]);

  if (!visible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'sticky',
        top: '24px',
        maxHeight: 'calc(100vh - 150px)',
        overflowY: 'auto',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px'
      }}
    >
      <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>
        {t(`${namespace}.form.preview_title`)}
      </div>

      {localError && (
        <Alert
          message={t(`${namespace}.detail.render_error`)}
          description={localError}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {!localHtml && !localError && (
        <Alert
          message={t(`${namespace}.form.preview_empty`)}
          type="info"
          showIcon
        />
      )}

      {localHtml && !localError && (
        <TemplateIframe
          ref={iframeRef}
          html={localHtml}
          paperSize={paperSize}
          cssVariablePrefix={cssVariablePrefix}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            ...iframeStyle
          }}
        />
      )}
    </div>
  );
};

export default TemplatePreviewSplitPane;
